import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/hooks/useLanguage";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const Destinations = () => {
  const { t, language } = useLanguage();
  
  const { data: destinations, isLoading } = useQuery({
    queryKey: ["destinations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("destinations")
        .select("*")
        .eq("is_active", true)
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Safari Destinations",
    "description": "Explore incredible safari destinations in Southern Africa",
    "itemListElement": destinations?.map((dest, index) => ({
      "@type": "Place",
      "position": index + 1,
      "name": dest.name,
      "description": dest.short_description,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": dest.country
      }
    })) || []
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="African Safari Destinations | Namibia, Botswana, Zimbabwe, South Africa & Lesotho"
        description="Explore Southern Africa's top safari destinations: Namibia's Sossusvlei dunes & Etosha, Botswana's Okavango Delta & Chobe, Zimbabwe's Victoria Falls, South Africa's Kruger Park & Garden Route, Lesotho's mountains. Expert destination guides with best visiting times & highlights."
        keywords="Namibia safari destinations, Botswana Okavango Delta, Victoria Falls Zimbabwe, Kruger National Park, Etosha National Park Namibia, Sossusvlei dunes, Chobe National Park, Garden Route South Africa, Lesotho Kingdom Sky, Moremi Game Reserve, Savuti Botswana, Linyanti wildlife, Table Mountain Cape Town"
        structuredData={structuredData}
        canonical="/destinations"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">{t("destinations.title")}</h1>
            <p className="text-xl text-muted-foreground">
              {t("destinations.subtitle")}
            </p>
          </div>
        </div>
      </section>

      {/* Destinations Grid */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <Skeleton className="w-full h-64" />
                  <CardHeader>
                    <Skeleton className="h-8 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-20 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {destinations?.map((destination) => {
                const name = language === "es" ? destination.name : language === "en" ? (destination.name_en || destination.name) : (destination.name_fr || destination.name);
                const country = language === "es" ? destination.country : language === "en" ? (destination.country_en || destination.country) : (destination.country_fr || destination.country);
                const shortDesc = language === "es" ? destination.short_description : language === "en" ? (destination.short_description_en || destination.short_description) : (destination.short_description_fr || destination.short_description);
                const fullDesc = language === "es" ? destination.full_description : language === "en" ? (destination.full_description_en || destination.full_description) : (destination.full_description_fr || destination.full_description);
                const bestTime = language === "es" ? destination.best_time_to_visit : language === "en" ? (destination.best_time_to_visit_en || destination.best_time_to_visit) : (destination.best_time_to_visit_fr || destination.best_time_to_visit);
                
                return (
                  <Card key={destination.id} className="overflow-hidden hover:shadow-2xl transition-all">
                    <Carousel 
                      className="w-full"
                      plugins={[
                        Autoplay({
                          delay: 4000,
                          stopOnInteraction: true,
                        }),
                      ]}
                    >
                      <CarouselContent>
                        {destination.gallery_images && destination.gallery_images.length > 0 ? (
                          destination.gallery_images.map((imageUrl: string, index: number) => (
                            <CarouselItem key={index}>
                              <img
                                src={imageUrl}
                                alt={`${name} - ${index + 1}`}
                                className="w-full h-96 object-cover"
                              />
                            </CarouselItem>
                          ))
                        ) : (
                          <CarouselItem>
                            <div className="w-full h-96 bg-muted flex items-center justify-center text-muted-foreground">
                              Sin imágenes disponibles
                            </div>
                          </CarouselItem>
                        )}
                      </CarouselContent>
                      {destination.gallery_images && destination.gallery_images.length > 1 && (
                        <>
                          <CarouselPrevious className="left-4" />
                          <CarouselNext className="right-4" />
                        </>
                      )}
                    </Carousel>
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <CardTitle className="text-3xl">{name}</CardTitle>
                        <Badge className="bg-primary">{country}</Badge>
                      </div>
                      <CardDescription className="text-lg">
                        {shortDesc}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <p className="text-muted-foreground leading-relaxed">
                        {fullDesc}
                      </p>
                      
                      {bestTime && (
                        <div>
                          <h4 className="font-semibold mb-2 text-primary">{t("destinations.bestTime")}</h4>
                          <p className="text-muted-foreground">{bestTime}</p>
                        </div>
                      )}

                      {destination.highlights && destination.highlights.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-3 text-primary">{t("destinations.highlights")}</h4>
                          <div className="flex flex-wrap gap-2">
                            {destination.highlights.map((highlight, index) => (
                              <Badge key={index} variant="secondary">
                                {highlight}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Destinations;
